'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { tx, useCurrentUser, useLocale, useBlockPart } from '@akademi/site-builder-runtime';
import { Button, Card, Input } from '@akademi/ui';
import { sectionInner, cardReset } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { ActivityApplicationModal } from './ActivityApplicationModal';
import { IneligibleInfo } from './IneligibleInfo';
import { BlockRoot } from '../_shared/BlockRoot';

// ---------------------------------------------------------------------------
// Shared types (re-exported so ActivityRow can import from here)
// ---------------------------------------------------------------------------

export interface ActivityFilter {
  eventTypeIds?: Array<number | string>;
  cityIds?: Array<number | string>;
  categoryIds?: Array<number | string>;
  educationLevels?: string[];
  statuses?: string[] | null;
  searchTerm?: string;
}

export interface ActivityBlockProps {
  /** Section heading. Default: "Activity". Localizable. */
  title?: string | Record<string, string>;
  /** How many items to display per tab / view. Default: 3. */
  maxShow?: number;
  /** Pagination offset forwarded to the API. Default: 0. */
  firstResult?: number;
  /** Show a search input in the block header. Pressing Enter writes ?term= to the URL. */
  showSearch?: boolean;
  /** Placeholder text for the search input. Localizable. */
  searchPlaceholder?: string | Record<string, string>;
  /** Message shown when there are no matching activities. Localizable. */
  emptyText?: string | Record<string, string>;
  /** Label for the "show more" action at the bottom. Localizable. */
  showMoreText?: string | Record<string, string>;
  /**
   * Icon/suffix next to the show-more label.
   * Accepts a text symbol (e.g. "→") or an image URL.
   * Localizable.
   */
  showMoreIcon?: string | Record<string, string>;
  /** Link target for the show-more button. Hidden when empty. */
  showMoreHref?: string;
  /**
   * When true, renders bucket switcher tabs using the shadcn tab visual style.
   * When false (default) all three buckets are shown as stacked sections.
   */
  tabMode?: boolean;
  /** Default active tab when tabMode = true. Default: upcoming. */
  bucket?: 'upcoming' | 'missed' | 'previous' | 'all';
  /** Tab / section title for upcoming events. Localizable. */
  upcomingTitle?: string | Record<string, string>;
  /** Tab / section title for missed events. Localizable. */
  missedTitle?: string | Record<string, string>;
  /** Tab / section title for previous events. Localizable. */
  previousTitle?: string | Record<string, string>;
  /**
   * Auth-gate the whole block. 'any' (default) always renders;
   * 'in' only for signed-in users; 'out' only for anonymous visitors.
   */
  /** Suffix shown after the item count in each section header. Localizable. */
  resultSuffix?: string | Record<string, string>;
  /** Label for the detail/inspect button. Localizable. */
  detailLabel?: string | Record<string, string>;
  /** Label for the apply button. Localizable. */
  applyLabel?: string | Record<string, string>;
  /** Label for the pending/awaiting-approval state. Localizable. */
  pendingLabel?: string | Record<string, string>;
  /** Prefix shown before the course code value. Localizable. */
  courseCodeLabel?: string | Record<string, string>;
  /** Prefix shown before the last entry date. Localizable. */
  lastEntryLabel?: string | Record<string, string>;
  /** Suffix shown after the duration number (e.g. "Saat"). Localizable. */
  hourLabel?: string | Record<string, string>;
  requireAuth?: 'any' | 'in' | 'out';
  filter?: ActivityFilter;
  sortField?: string;
  sortOrder?: 'ASCENDING' | 'DESCENDING';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

interface Activity {
  id: number | string;
  title?: string;
  description?: string;
  startDate?: number;
  endDate?: number;
  lastEntryDate?: number;
  url?: string;
  city?: string;
  maxEntries?: number;
  duration?: number;
  courseCode?: string;
  status?: string;
  educationLevels?: string[];
  thumbnail?: string;
  entriesClosed?: boolean;
  quotaFull?: boolean;
  registered?: boolean;
  /**
   * 9476 SHOW_BLOCKED — false means the tenant screens eligibility and this
   * viewer fails it. Undefined is "not screened", never "ineligible".
   */
  eligible?: boolean;
  /** Backend-localised reason behind `eligible: false`. */
  ineligibleReason?: string;
  /** Every reason behind `eligible: false` — what the info badge lists. */
  ineligibleReasons?: string[];
  /** Dynamic application form id (9464) — forwarded to the apply modal. */
  applicationFormId?: number;
}

interface ActivitiesResponse {
  upcoming?: Activity[];
  missed?: Activity[];
  previous?: Activity[];
  totals?: { upcoming: number; missed: number; previous: number };
}

type Bucket = 'upcoming' | 'missed' | 'previous';

interface CardLabels {
  detail: string;
  apply: string;
  pending: string;
  /** 9476 SHOW_BLOCKED — the disabled apply control's text. */
  ineligible: string;
  /** 9476 — heading of the popover that spells out WHY the viewer is blocked. */
  ineligibleWhy: string;
  /** 9476 — accessible name / tooltip of the info badge that opens it. */
  ineligibleWhyHint: string;
  courseCode: string;
  lastEntry: string;
  hour: string;
}

// ---------------------------------------------------------------------------
// URL param helpers
// ---------------------------------------------------------------------------

function parseCommaParam(params: URLSearchParams, key: string): string[] {
  return (
    params
      .get(key)
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean) ?? []
  );
}

function mergeUniq(...arrays: (string[] | undefined)[]): string[] {
  return [...new Set(arrays.flatMap((a) => a ?? []))];
}

/**
 * Row count for {@code b} — drives whether "load more" shows.
 *
 * <p>A SHORT page ({@code pageCount < max}) is the end of the feed: clamp to
 * {@code loaded} so the button hides. {@code totals.totalRecords} is only
 * trusted while pages come back full — it has been seen to over-count, leaving
 * the button up forever with every click fetching nothing.
 */
function bucketTotal(
  data: ActivitiesResponse,
  b: Bucket | 'all',
  loaded: number,
  pageCount: number,
  max: number,
): number {
  if (pageCount < max) return loaded;
  const t = data.totals;
  if (t) {
    return b === 'all' ? (t.upcoming ?? 0) + (t.missed ?? 0) + (t.previous ?? 0) : (t[b] ?? 0);
  }
  return loaded + 1;
}

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

async function fetchActivities(
  body: Record<string, unknown>,
  signal: AbortSignal,
): Promise<ActivitiesResponse | null> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/api/activities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  return (await res.json()) as ActivitiesResponse;
}

function pickBucket(data: ActivitiesResponse, bucket: Bucket | 'all'): Activity[] {
  const up = data.upcoming ?? [];
  const missed = data.missed ?? [];
  const prev = data.previous ?? [];
  switch (bucket) {
    case 'missed':
      return missed;
    case 'previous':
      return prev;
    case 'all':
      return [...up, ...missed, ...prev];
    case 'upcoming':
    default:
      return up;
  }
}

export function ActivityBlock({
  title,
  maxShow,
  firstResult,
  showSearch = false,
  searchPlaceholder,
  emptyText,
  showMoreText,
  showMoreIcon,
  showMoreHref: _showMoreHref,
  tabMode = false,
  bucket = 'upcoming',
  upcomingTitle,
  missedTitle,
  previousTitle,
  resultSuffix,
  detailLabel,
  applyLabel,
  pendingLabel,
  courseCodeLabel,
  lastEntryLabel,
  hourLabel,
  requireAuth,
  filter,
  sortField,
  sortOrder,
  paddingOuter,
  paddingInner,
}: ActivityBlockProps) {
  const { locale } = useLocale();
  const { t } = useTranslation('common');
  const part = useBlockPart();
  const currentUser = useCurrentUser();
  const router = useRouter();
  const searchParams = useSearchParams();

  // ── Search input (local state mirrors ?term= on mount) ───────────────────
  const [searchInput, setSearchInput] = useState(searchParams.get('term') ?? '');
  const placeholder = t('activity.searchPlaceholder') || tx(searchPlaceholder);

  function pushTerm(value: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set('term', value);
    else params.delete('term');
    router.push(`?${params.toString()}`, { scroll: false });
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') pushTerm('');
  }

  function handleSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') pushTerm(searchInput.trim());
  }

  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);

  const defaultTab: Bucket = bucket === 'all' ? 'upcoming' : (bucket as Bucket);
  const [activeTab, setActiveTab] = useState<Bucket>(defaultTab);

  const first = clamp(typeof firstResult === 'number' ? firstResult : 0, 0, 10_000);
  const max = clamp(typeof maxShow === 'number' ? maxShow : 3, 1, 50);
  const sort = (sortField ?? '').trim() || 'recent';
  const order = sortOrder === 'DESCENDING' ? 'DESCENDING' : 'ASCENDING';

  // ── i18n tab / section titles ────────────────────────────────────────────
  const tabDefs: { key: Bucket; label: string }[] = [
    { key: 'upcoming', label: t('activity.upcoming') || tx(upcomingTitle) },
    { key: 'missed', label: t('activity.missed') || tx(missedTitle) },
    { key: 'previous', label: t('activity.previous') || tx(previousTitle) },
  ];

  // ── Merge prop filter with URL search params ─────────────────────────────
  const mergedFilter = {
    eventTypeIds: mergeUniq(
      filter?.eventTypeIds?.map(String),
      parseCommaParam(searchParams, 'eventType'),
    ),
    cityIds: mergeUniq(filter?.cityIds?.map(String), parseCommaParam(searchParams, 'city')),
    categoryIds: mergeUniq(
      filter?.categoryIds?.map(String),
      parseCommaParam(searchParams, 'category'),
    ),
    educationLevels: mergeUniq(
      filter?.educationLevels,
      parseCommaParam(searchParams, 'educationLevel'),
    ),
    statuses: filter?.statuses ?? null,
    searchTerm: searchParams.get('term')?.trim() || filter?.searchTerm || '',
  };

  // Stable key — drives fetch reset when any query param changes
  const baseBody = {
    maxResults: max,
    sortField: sort,
    sortOrder: order,
    filter: mergedFilter,
  };
  const bodyKey = JSON.stringify({ ...baseBody, first });

  // ── Unified state — all 3 buckets in both modes ──────────────────────────
  const EMPTY_ITEMS: Record<Bucket, Activity[]> = { upcoming: [], missed: [], previous: [] };
  const EMPTY_TOTALS: Record<Bucket, number> = { upcoming: 0, missed: 0, previous: 0 };
  const EMPTY_LOADING: Record<Bucket, boolean> = {
    upcoming: false,
    missed: false,
    previous: false,
  };

  const [bktItems, setBktItems] = useState<Record<Bucket, Activity[]>>(EMPTY_ITEMS);
  const [bktTotals, setBktTotals] = useState<Record<Bucket, number>>(EMPTY_TOTALS);
  const [bktLoadingMore, setBktLoadingMore] = useState<Record<Bucket, boolean>>(EMPTY_LOADING);
  const [loading, setLoading] = useState(!hidden);
  const [loadError, setLoadError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // ── Initial fetch / reset on filter change ───────────────────────────────
  useEffect(() => {
    if (hidden) {
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setLoadError(false);
    setBktItems(EMPTY_ITEMS);
    setBktTotals(EMPTY_TOTALS);
    setBktLoadingMore(EMPTY_LOADING);

    fetchActivities({ firstResult: first, ...baseBody }, ctrl.signal).then((data) => {
      if (ctrl.signal.aborted) return;
      if (!data) {
        setLoadError(true);
        setLoading(false);
        return;
      }
      const up = (data.upcoming ?? []).slice(0, max);
      const mi = (data.missed ?? []).slice(0, max);
      const pr = (data.previous ?? []).slice(0, max);
      setBktItems({ upcoming: up, missed: mi, previous: pr });
      setBktTotals({
        upcoming: bucketTotal(data, 'upcoming', up.length, up.length, max),
        missed: bucketTotal(data, 'missed', mi.length, mi.length, max),
        previous: bucketTotal(data, 'previous', pr.length, pr.length, max),
      });
      setLoading(false);
    });

    return () => ctrl.abort();
  }, [bodyKey, hidden]);

  // ── Per-bucket load-more ─────────────────────────────────────────────────
  async function handleLoadMore(bkt: Bucket) {
    if (bktLoadingMore[bkt]) return;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBktLoadingMore((prev) => ({ ...prev, [bkt]: true }));
    try {
      const data = await fetchActivities(
        { firstResult: bktItems[bkt].length, ...baseBody },
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      if (data) {
        const page = pickBucket(data, bkt).slice(0, max);
        const loaded = bktItems[bkt].length + page.length;
        if (page.length > 0) {
          setBktItems((prev) => ({ ...prev, [bkt]: [...prev[bkt], ...page] }));
        }
        setBktTotals((prev) => ({
          ...prev,
          [bkt]: bucketTotal(data, bkt, loaded, page.length, max),
        }));
      }
    } finally {
      if (!ctrl.signal.aborted) setBktLoadingMore((prev) => ({ ...prev, [bkt]: false }));
    }
  }

  // ── Render helpers ───────────────────────────────────────────────────────
  if (hidden) return null;

  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);

  const heading = t('activity.blockTitle') || tx(title);
  const emptyMsg = t('activity.empty') || tx(emptyText);
  const moreText = t('button.showMore') || tx(showMoreText);
  const countLabel = t('activity.activitiesFound') || tx(resultSuffix);
  const moreIconRaw = tx(showMoreIcon);

  /** i18next echoes the key itself when it is missing, so a bare `||` never
   *  reaches the fallback — compare against the key before trusting it. */
  const tOr = (key: string, fallback: string) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const cardLabels: CardLabels = {
    detail: t('activity.viewDetails') || tx(detailLabel),
    apply: t('activity.apply') || tx(applyLabel),
    pending: t('activity.pendingApproval') || tx(pendingLabel),
    ineligible: t('activity.ineligible') || 'Şartları Karşılamıyorsunuz',
    ineligibleWhy: tOr('activity.ineligibleReasonTitle', 'Neden başvuramıyorum?'),
    ineligibleWhyHint: tOr('activity.ineligibleReasonHint', 'Nedenini gör'),
    courseCode: t('activity.trainingCode') || tx(courseCodeLabel),
    lastEntry: t('activity.applicationDeadline') || tx(lastEntryLabel),
    hour: t('activity.hours') || tx(hourLabel),
  };

  const moreIconNode: ReactNode = moreIconRaw ? (
    isImageUrl(moreIconRaw) ? (
      <img src={moreIconRaw} alt="" width={16} height={16} style={showMoreIconImgStyle} />
    ) : (
      <span style={showMoreIconStyle}>{moreIconRaw}</span>
    )
  ) : null;

  function renderShowMore(bkt: Bucket) {
    const isLoadingMore = bktLoadingMore[bkt];
    const hasMore = bktTotals[bkt] > bktItems[bkt].length;
    if (!hasMore) return null;
    return (
      <div style={showMoreWrapStyle}>
        <Button
          onClick={() => void handleLoadMore(bkt)}
          disabled={isLoadingMore}
          variant="brand-outlined"
          size="lg"
          {...part('pager')}
        >
          {isLoadingMore ? '…' : moreText}
          {!isLoadingMore && moreIconNode}
        </Button>
      </div>
    );
  }

  // ── Shared header renderer ───────────────────────────────────────────────
  function renderHeader(right?: ReactNode) {
    return (
      <header style={headerStyle} {...part('header')}>
        <div style={headerRightStyle}>
          {heading && (
            <h2 style={titleStyle} {...part('title')}>
              {heading}
            </h2>
          )}
          {right}
        </div>
        {showSearch && (
          <Input
            type="search"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKey}
            placeholder={placeholder}
            className="akd-activity-search"
            style={searchInputStyle}
            {...part('search')}
          />
        )}
      </header>
    );
  }

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <BlockRoot as="div" style={outerPad}>
        <style>{HOVER_CSS}</style>
        <div style={{ ...sectionInner, ...innerPad }}>
          {renderHeader()}
          {tabMode && <TabBar tabs={tabDefs} active={activeTab} onSelect={setActiveTab} />}
          <div style={gridStyle}>
            {Array.from({ length: Math.min(max, 3) }).map((_, i) => (
              <Card key={i} style={{ ...cardReset, ...cardStyle, opacity: 0.5, minHeight: 168 }} />
            ))}
          </div>
        </div>
      </BlockRoot>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (loadError) {
    if (!showSearch) return null;
    return (
      <BlockRoot as="div" style={outerPad}>
        <div style={{ ...sectionInner, ...innerPad }}>
          {renderHeader()}
          <p style={messageStyle}>{t('activity.loadError')}</p>
        </div>
      </BlockRoot>
    );
  }

  // ── tabMode: tab bar with per-tab load-more ───────────────────────────────
  if (tabMode) {
    const visibleTabs = tabDefs.filter((t) => bktItems[t.key].length > 0 || bktTotals[t.key] > 0);
    if (!showSearch && visibleTabs.length === 0) return null;
    const displayTabs = visibleTabs.length > 0 ? visibleTabs : tabDefs;

    const firstVisible = displayTabs[0]!.key;
    const effectiveTab = displayTabs.find((t) => t.key === activeTab) ? activeTab : firstVisible;
    const items = bktItems[effectiveTab];
    const total = bktTotals[effectiveTab];

    return (
      <BlockRoot as="div" style={outerPad}>
        <style>{HOVER_CSS}</style>
        <div style={{ ...sectionInner, ...innerPad }}>
          {renderHeader()}
          <TabBar tabs={displayTabs} active={effectiveTab} onSelect={setActiveTab} />
          {total > 0 && (
            <p style={countStyle}>
              {total} {countLabel}
            </p>
          )}
          {items.length === 0 ? (
            <p style={messageStyle}>{emptyMsg}</p>
          ) : (
            <div style={listStyle} {...part('list')}>
              {items.map((item) => (
                <ActivityCard key={item.id} item={item} locale={locale} labels={cardLabels} />
              ))}
            </div>
          )}
          {renderShowMore(effectiveTab)}
        </div>
      </BlockRoot>
    );
  }

  // ── Non-tabMode: 3 stacked sections ──────────────────────────────────────
  const allEmpty = tabDefs.every((t) => bktItems[t.key].length === 0 && bktTotals[t.key] === 0);

  if (allEmpty) {
    if (!showSearch) return null;
    return (
      <BlockRoot as="div" style={outerPad}>
        <div style={{ ...sectionInner, ...innerPad }}>
          {renderHeader()}
          <p style={messageStyle}>{emptyMsg}</p>
        </div>
      </BlockRoot>
    );
  }

  const visibleSections = tabDefs.filter((t) => bktItems[t.key].length > 0 || bktTotals[t.key] > 0);
  const grandTotal = tabDefs.reduce((sum, t) => sum + (bktTotals[t.key] ?? 0), 0);

  return (
    <BlockRoot as="div" style={outerPad}>
      <style>{HOVER_CSS}</style>
      <div style={{ ...sectionInner, ...innerPad }}>
        {renderHeader(
          <span style={countStyle}>
            {grandTotal} {countLabel}
          </span>,
        )}
        {visibleSections.map(({ key, label }, si) => (
          <section key={key} style={bucketSectionStyle}>
            <div style={bucketHeaderRowStyle}>
              <h3 style={bucketTitleStyle}>{label}</h3>
              {bktTotals[key] > 0 && (
                <span style={countStyle}>
                  {bktTotals[key]} {countLabel}
                </span>
              )}
            </div>
            <div style={listStyle} {...part('list', si)}>
              {bktItems[key].map((item) => (
                <ActivityCard key={item.id} item={item} locale={locale} labels={cardLabels} />
              ))}
            </div>
            {renderShowMore(key)}
          </section>
        ))}
      </div>
    </BlockRoot>
  );
}

// ---------------------------------------------------------------------------
// Tab bar (shadcn "line" variant visual style via inline CSS + theme tokens)
// ---------------------------------------------------------------------------

function TabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: { key: Bucket; label: string }[];
  active: Bucket;
  onSelect: (key: Bucket) => void;
}): ReactNode {
  const part = useBlockPart();
  return (
    <div className="akd-activity-tabs" style={tabListStyle} role="tablist" {...part('tablist')}>
      {tabs.map((t, i) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={active === t.key}
          onClick={() => onSelect(t.key)}
          style={tabTriggerStyle(active === t.key)}
          {...part('tab', i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

const EDU_LABELS: Record<string, string> = {
  PRIMARY_SCHOOL: 'İlköğretim',
  MIDDLE_SCHOOL: 'Ortaöğretim',
  HIGH_SCHOOL: 'Lise',
  ASSOCIATE_DEGREE: 'Ön Lisans',
  BACHELORS_DEGREE: 'Lisans',
  MS_MBA: 'Yüksek Lisans',
  DOCTORAL: 'Doktora',
};

function ActivityCard({
  item,
  locale,
  labels,
}: {
  item: Activity;
  locale: string;
  labels: CardLabels;
}): ReactNode {
  const currentUser = useCurrentUser();
  const isSignedIn = currentUser != null;
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  const dateRange = formatRange(item.startDate, item.endDate, locale);
  const deadlineFull = item.lastEntryDate ? formatFull(item.lastEntryDate, locale) : '';
  const deadlineDay = item.lastEntryDate ? formatDay(item.lastEntryDate, locale) : '';
  const ctaHref = isUrl(item.url) ? item.url : undefined;
  // The detail link accepts any non-empty url value (not just http(s)),
  // since the backend may return a relative path / slug.
  const detailHref = typeof item.url === 'string' && item.url.trim() ? item.url.trim() : undefined;
  const durationH = item.duration ? Math.round(item.duration / 60) : null;
  const eduText = item.educationLevels?.map((e) => EDU_LABELS[e] ?? e).join(', ');
  const slotsOpen = !item.entriesClosed && !item.quotaFull;
  const alreadyRegistered = Boolean(item.registered) || applied;
  const reasons = item.ineligibleReasons ?? (item.ineligibleReason ? [item.ineligibleReason] : []);

  return (
    <Card asChild>
      <article className="akd-activity-card" style={{ ...cardReset, ...rowCardStyle }}>
        {/* Thumbnail */}
        <div className="akd-activity-thumb" style={rowThumbWrapStyle}>
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              style={rowThumbImgStyle}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div style={rowThumbPlaceholderStyle}>
              <IconCalendar />
            </div>
          )}
        </div>

        {/* Centre content */}
        <div style={rowBodyStyle}>
          {item.city && (
            <div style={rowLocationStyle}>
              <IconMapPin />
              {item.city}
            </div>
          )}
          {ctaHref ? (
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="akd-activity-cta"
              style={rowTitleLinkStyle}
            >
              {item.title}
            </a>
          ) : (
            <div style={rowTitleStyle}>{item.title}</div>
          )}
          <div style={rowMetaListStyle}>
            {dateRange && (
              <span style={metaItemStyle}>
                <IconCalendar />
                {dateRange}
              </span>
            )}
            {durationH !== null && (
              <span style={metaItemStyle}>
                <IconClock />
                {durationH} {labels.hour}
              </span>
            )}
            {eduText && (
              <span style={metaItemStyle}>
                <IconGraduation />
                {eduText}
              </span>
            )}
          </div>
        </div>

        {/* Right actions */}
        <div className="akd-activity-actions" style={rowActionsStyle}>
          <div style={rowBtnsStyle}>
            <Button
              {...(detailHref
                ? { href: detailHref, target: '_blank', rel: 'noopener noreferrer' }
                : {})}
              variant="brand"
              size="sm"
            >
              <IconInfo />
              {labels.detail}
            </Button>
            {alreadyRegistered ? (
              <Button disabled variant="secondary" size="sm">
                {labels.pending}
              </Button>
            ) : !slotsOpen ? null : item.eligible === false ? (
              // 9476 SHOW_BLOCKED — listed, but not applicable by this viewer.
              // The badge beside it opens the backend's own reasons, so the
              // refusal says which rule was tripped instead of only that one was.
              <span style={ineligibleWrapStyle}>
                <Button
                  disabled
                  variant="secondary"
                  size="sm"
                  title={reasons.join(' · ') || undefined}
                  style={ineligibleBtnStyle}
                >
                  {labels.ineligible}
                </Button>
                <IneligibleInfo
                  reasons={reasons}
                  title={labels.ineligibleWhy}
                  triggerLabel={labels.ineligibleWhyHint}
                />
              </span>
            ) : isSignedIn ? (
              <Button variant="info" size="sm" onClick={() => setApplyOpen(true)}>
                {labels.apply}
              </Button>
            ) : (
              <Button href="/sign-in" variant="info" size="sm">
                {labels.apply}
              </Button>
            )}
          </div>
          <div style={rowMetaRightStyle}>
            {item.courseCode && (
              <span>
                {labels.courseCode} {item.courseCode}
              </span>
            )}
            {deadlineFull && (
              <span>
                {labels.lastEntry}{' '}
                <strong style={{ color: 'var(--color-brand-600, #c0006a)' }}>{deadlineDay}</strong>
              </span>
            )}
          </div>
        </div>

        {isSignedIn && slotsOpen && !alreadyRegistered && item.eligible !== false ? (
          <ActivityApplicationModal
            open={applyOpen}
            onOpenChange={setApplyOpen}
            eventId={item.id}
            applicationFormId={item.applicationFormId}
            eventTitle={item.title}
            eventSubtitle={item.city}
            onSuccess={() => setApplied(true)}
          />
        ) : null}
      </article>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatDay(ms: number, locale: string): string {
  try {
    return new Date(ms).toLocaleDateString(locale || 'en', { day: 'numeric', month: 'long' });
  } catch {
    return '';
  }
}

function formatFull(ms: number, locale: string): string {
  try {
    return new Date(ms).toLocaleDateString(locale || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function formatRange(start?: number, end?: number, locale = 'en'): string {
  if (!start) return '';
  const s = new Date(start);
  if (!end) {
    return s.toLocaleDateString(locale || 'en', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  const e = new Date(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const startStr = s.toLocaleDateString(locale || 'en', {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const endStr = e.toLocaleDateString(locale || 'en', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${startStr} — ${endStr}`;
}

function isUrl(v?: string): v is string {
  return typeof v === 'string' && /^https?:\/\//i.test(v.trim());
}

function isImageUrl(v: string): boolean {
  return /^https?:\/\//i.test(v.trim()) || v.trim().startsWith('/');
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  style: { flexShrink: 0 } as CSSProperties,
};

function IconCalendar(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function IconMapPin(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function IconClock(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}
function IconGraduation(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  );
}
function IconInfo(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const HOVER_CSS = `
.akd-activity-card{transition:transform .15s ease,box-shadow .15s ease,border-color .15s ease}
.akd-activity-card:hover{transform:translateY(-2px);border-color:var(--color-brand-500,#3b5bdb)}
.akd-activity-cta{transition:transform .15s ease,filter .15s ease}
.akd-activity-cta:hover{filter:brightness(.94);transform:translateY(-1px)}
@media(max-width:900px){
  .akd-activity-card{flex-direction:column!important;gap:12px!important}
  .akd-activity-card:hover{transform:none!important}
  .akd-activity-thumb{width:100%!important;height:auto!important;aspect-ratio:16/9}
  .akd-activity-actions{flex-direction:row!important;flex-wrap:wrap!important;min-width:0!important;width:100%!important;justify-content:flex-end!important;align-items:center!important}
  .akd-activity-search{width:100%!important}
  .akd-activity-tabs{overflow-x:auto;scrollbar-width:none}
  .akd-activity-tabs::-webkit-scrollbar{display:none}
}
`;

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  paddingBottom: 14,
  marginBottom: 16,
  borderBottom: '2px solid var(--color-border-subtle, #e5e7eb)',
};

const headerRightStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 4,
};

const searchInputStyle: CSSProperties = {
  height: 36,
  padding: '0 12px',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  borderRadius: 'var(--radius-md, 6px)',
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-primary, #111)',
  background: 'var(--color-surface-card, #fff)',
  outline: 'none',
  width: 220,
  flexShrink: 0,
  boxSizing: 'border-box' as const,
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xl, 22px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111827)',
  margin: 0,
};

// Tab bar — shadcn "line" variant visual style via theme tokens
const tabListStyle: CSSProperties = {
  display: 'flex',
  gap: 0,
  borderBottom: '1px solid var(--color-border-subtle, #e5e7eb)',
  marginBottom: 16,
};

const tabTriggerStyle = (active: boolean): CSSProperties => ({
  padding: '8px 14px',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: active ? 600 : 400,
  color: active ? 'var(--color-brand-500, #3b5bdb)' : 'var(--color-text-secondary, #5f6675)',
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid var(--color-brand-500, #3b5bdb)' : '2px solid transparent',
  marginBottom: -1,
  cursor: 'pointer',
  transition: 'color .15s ease, border-color .15s ease',
  whiteSpace: 'nowrap',
});

const gridStyle: CSSProperties = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
};

// `cardStyle` (skeleton) and `rowCardStyle` (the `.akd-activity-card`
// `<article>`) are this block's own card surface, spread after the shared
// `cardReset` onto `<Card>` / `<Card asChild>`.
const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--color-surface-card, #fff)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  borderRadius: 'var(--radius-lg, 12px)',
  overflow: 'hidden',
};

// ── Row card styles ───────────────────────────────────────────────────────────

const rowCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 16,
  background: 'var(--color-surface-card, #fff)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  borderRadius: 'var(--radius-lg, 12px)',
  padding: 16,
  overflow: 'hidden',
};

const rowThumbWrapStyle: CSSProperties = {
  flexShrink: 0,
  aspectRatio: 'var(--app-activity-card-image-aspect-ratio, 2)',
  height: 120,
  borderRadius: 'var(--radius-md, 8px)',
  overflow: 'hidden',
};

const rowThumbImgStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const rowThumbPlaceholderStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  minHeight: 90,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-brand-400, #93a8f5)',
};

const rowBodyStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const rowLocationStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-secondary, #6b7280)',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: 'var(--font-tracking-wider, 0.04em)',
};

const rowTitleLinkStyle: CSSProperties = {
  fontSize: 'var(--font-size-base, 15px)',
  fontWeight: 600,
  color: 'var(--color-brand-600, #c0006a)',
  textDecoration: 'none',
  lineHeight: 1.4,
};

const rowTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-base, 15px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111)',
  lineHeight: 1.4,
};

const rowMetaListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  marginTop: 4,
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const metaItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};

const rowActionsStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 10,
  minWidth: 160,
};

const rowBtnsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'stretch',
};

/** Keeps the info badge on the same line as the disabled apply control
 *  without breaking the column's stretched button width. */
const ineligibleWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  maxWidth: 220,
  color: 'var(--color-text-secondary, #6b7280)',
};

/** The 9476 label is a full sentence, not the verb phrase every other action
 *  carries, and the shared Button is `whitespace-nowrap` + `shrink-0` — left
 *  alone it stretches the action column and spills past the card. Wrap it to
 *  two compact lines instead. */
const ineligibleBtnStyle: CSSProperties = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  textAlign: 'center',
  minWidth: 0,
  height: 'auto',
  minHeight: 32,
  paddingBlock: 5,
  fontSize: 'var(--font-size-xs, 12px)',
  lineHeight: 1.3,
};

const rowMetaRightStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 3,
  fontSize: 'var(--font-size-xs, 11px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const messageStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #5f6675)',
  padding: 'var(--space-6, 24px) 0',
  textAlign: 'center',
};

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const countStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-secondary, #6b7280)',
  margin: '0 0 12px',
};

const bucketSectionStyle: CSSProperties = {
  marginTop: 'var(--space-8, 32px)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  borderRadius: 'var(--radius-lg, 12px)',
  padding: '20px 20px 16px',
  background: 'var(--color-surface-card, #fff)',
};

const bucketHeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 'var(--space-4, 16px)',
};

const bucketTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg, 1.125rem)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111)',
  margin: 0,
};

const showMoreWrapStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'var(--space-6, 24px)',
};

const showMoreIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
};

const showMoreIconImgStyle: CSSProperties = {
  display: 'block',
  width: 16,
  height: 16,
  objectFit: 'contain',
  flexShrink: 0,
};
